import { exceptionInfo } from '../common/types.ts';
import type { Identity, PeerId, Restrictions, Source } from './types/common.ts';
import {DomainKeys, Options, Peer, Peers, State} from './types/state.ts';
import {check} from '../restrictions.ts';
import * as c from './core.ts';
import {castDraft, Draft, produce} from 'immer';
import type { DomainUri } from './types/domain-registry.ts';
import type { MessageType, SendMessageAction } from '../node.ts';
import { peerAdded, peerRemoved } from '../common/messages.ts';
import type { HandleReturnType } from '../domain.ts';
import type { Reason } from '../reason.ts';
import {some, objectAsKey, WithRequired} from '../common/utilities.ts';

function isSourceSubsetOf(source, subset): boolean {
    switch (subset.type) {
        case 'node':
            return subset.node === source.node;
        case 'peer':
            return (subset.node === source.node) && (subset.peer === source.peer);
        case 'local':
            return (subset.receive === source.receive);
        default:
            return false;
    }
}

export function bySourceInDomain<D extends DomainKeys>(state: State, source: Source, domain: D): Array<JoinedPeer<D>> {
    return peersInDomain(state, domain).filter(peer => isSourceSubsetOf(peer.source, source));
}
export function bySource(state: State, source: Source): Array<Peer> {
    return peers(state).filter(peer => isSourceSubsetOf(peer.source, source));
}

// todo move this type in types/common.ts
export type JoinedPeer<D extends DomainKeys> = WithRequired<Peer, D>

export function byIdInDomain<D extends DomainKeys>(state: State, peerId: PeerId | undefined, domain: D): JoinedPeer<D> | undefined {
    const peer = byId(state, peerId);
    if (peer?.[domain]) {
        return peer as JoinedPeer<D>;
    }
}

export function byId(state: State, peerId?: PeerId): Peer | undefined {
    return peerId ? state.peers?.[peerId] : undefined;
}

export function byIdThrows(state: State, peerId?: PeerId): Peer {
    if (peerId) {
        const peer = byId(state, peerId);
        if (peer) {
            return peer;
        } else {
            throw exceptionInfo(`Unable to find peer ${peerId}`, {});
        }
    } else {
        throw exceptionInfo(`Peer id is missing`, {});
    }
}

export function byIdInDomainThrows<D extends DomainKeys>(state: State, peerId: PeerId | undefined, domain: D): JoinedPeer<D> {
    if (peerId) {
        const peer = byIdInDomain<D>(state, peerId, domain);
        if (peer) {
            return peer;
        } else {
            throw exceptionInfo(`Unable to find peer ${peerId} in domain ${domain}`, {});
        }
    } else {
        throw exceptionInfo(`Peer id is missing`, {});
    }
}

export function isLocalPeer(peer?: Peer): boolean {
    return peer?.source.type === 'local';
}

export function isClusterPeer<D extends DomainKeys>(domain: D, peer?: Peer, ctx?: Identity): boolean {
    return check(peer?.[domain]?.restrictions, [peer?.source, peer?.identity], [{type: 'cluster'}, ctx]);
}

export function byIdentity(state: State, identity: Identity): PeerId | undefined {
    const key = objectAsKey(identity);
    return state.identities?.get(key);
}

export function peers(state: State): Array<Peer> {
    return Object.values(state.peers || {});
}

export function peersInDomain<D extends DomainKeys>(state: State, domain: D): Array<JoinedPeer<D>> {
    return Array.from<PeerId, JoinedPeer<D> | undefined>(state.domains?.[domain] ?? [], (peerId: PeerId) => {
        return byIdInDomain<D>(state, peerId, domain);
    }).filter(some<JoinedPeer<D>>);
}

export function ensurePeerWithId(state: State, source: Source, peerId: PeerId, resolvedIdentity: Identity, creationRequest?, options?: Options): [State, Peer] {
    const p = byId(state, peerId);
    if (p) {
        return [state, p];
    }

    const newPeer: Peer = produce({id: peerId, identity: resolvedIdentity, source}, (draft: Draft<Peer>) => {
        if (options) {
            draft.options = options;
        }
        if (creationRequest) {
            draft.creationRequest = creationRequest;
        }
    });
    return [produce(state, (draft: Draft<State>) => {

        // const user = resolvedIdentity.user ?? noUser;
        draft.users = draft.users || {};
        if (resolvedIdentity.user) {
            draft.users.byName = draft.users.byName || new Map();
            const userPeers = draft.users.byName.get(resolvedIdentity.user);
            if (userPeers) {
                userPeers.add(peerId);
            } else {
                draft.users.byName.set(resolvedIdentity.user, new Set<string>([peerId]));
            }
        } else {
            draft.users.noUser = draft.users.noUser || new Set<PeerId>();
            draft.users.noUser.add(peerId);
        }
        draft.identities = draft.identities || new Map<string, PeerId>();
        draft.identities.set(objectAsKey(resolvedIdentity), peerId);

        draft.peers = draft.peers || {};
        draft.peers[peerId] = castDraft(newPeer);
        if (options?.service) {
            draft.services = draft.services || new Set<PeerId>();
            draft.services.add(peerId)
        }

    }), newPeer];

}

export function removePeer<S extends State>(state: S, peer: Peer): S {
    const idnt = peer.identity;
    const id: PeerId = peer.id;
    const user: string | undefined = idnt.user;

    return produce(state, (draft: Draft<S>) => {
        if (draft.identities) {
            draft.identities.delete(objectAsKey(idnt));
        }
        if (draft.users) {
            if (user) {
                const userPeers = draft.users.byName?.get(user);
                if (userPeers) {
                    userPeers.delete(id);
                    if (userPeers.size == 0) {
                        draft.users.byName?.delete(user);
                    }
                }
                if (draft.users.byName && draft.users.byName.size === 0) {
                    delete draft.users.byName;
                }
            } else {
                draft.users.noUser?.delete(id);
                if (draft.users.noUser?.size === 0) {
                    delete draft.users.noUser;
                }
            }
        }
        if (draft.peers) {
            delete draft.peers[id];
        }
        draft.services = draft.services || new Set<PeerId>();
        if (draft.services) {
            draft.services.delete(id);
            // if (s.services.size == 0) {
            //     delete s.services;
            // }
        }
    });
}

export function setPeer<S extends State>(state: S, peerId: PeerId, peer: Peer): S {
    return produce(state, (s: Draft<S>) => {
        s.peers = s.peers || castDraft({} as Peers);
        s.peers[peerId] = castDraft(peer);
    });
}

export function updatePeer<P = Draft<Peer>>(state: State, peerId: PeerId, f: (draft: P) => void): State {
    return produce(state, (s: Draft<State>) => {
        s.peers![peerId] = produce(s.peers![peerId], f);
    })
}

export function isPeerVisibleIdentity(
    lhs: [source: Source | undefined, identity: Identity | undefined, restrictions?: Restrictions, service?: boolean],
    rhs: [source: Source | undefined, identity: Identity, restrictions?: Restrictions, service?: boolean]) {

    const [lhsSource, lhsIdentity, lhsRestrictions, lhsService] = lhs;
    const [rhsSource, rhsIdentity, rhsRestrictions, rhsService] = rhs;

    if (!lhsRestrictions && !rhsRestrictions) {
        const rhsUser = rhsIdentity?.user;
        const lhsUser = lhsIdentity?.user;
        return (rhsService || lhsService || (rhsUser === lhsUser));
    } else {
        return check(lhsRestrictions, [lhsSource, lhsIdentity], [rhsSource, rhsIdentity]) && check(rhsRestrictions, [rhsSource, rhsIdentity], [lhsSource, lhsIdentity]);
    }
}

/**
 * Returns whether two peers can see each other.
 * @param domain
 * @param lhsPeer
 * @param rhsPeer
 */
export function isPeerVisible<D extends DomainKeys>(domain: D, lhsPeer: JoinedPeer<D>, rhsPeer: JoinedPeer<D>): boolean {

    return (rhsPeer.id !== lhsPeer.id)
        && isPeerVisibleIdentity(
            [lhsPeer.source, lhsPeer.identity, lhsPeer[domain]?.restrictions, lhsPeer.options?.service],
            [rhsPeer.source, rhsPeer.identity, rhsPeer[domain]?.restrictions, rhsPeer.options?.service]);
}

/* Matches all visible peers disregarding their user */
function allVisiblePeers<D extends DomainKeys>(state: State, domain: D, peer: JoinedPeer<D>, includeSelf = false): Array<JoinedPeer<D>> {
    return peersInDomain(state, domain)
        .concat(
            Array.from(state.services ?? [], (peerId: PeerId) => {
                return byId(state, peerId) as JoinedPeer<D>; // todo do we use services at all???
            }).filter(some<JoinedPeer<D>>)
        ).filter(other => {
            return (includeSelf && (peer.id === other.id)) || isPeerVisible(domain, peer, other);
        });
}

/**
 * Returns a sequence of ids of the consumers that are seen by a provider
 */
export function visiblePeers<D extends DomainKeys>(state: State, domain: D, peer: JoinedPeer<D>, includeSelf?: boolean): Array<JoinedPeer<D>> {
    if (peer.options?.service) {
        return allVisiblePeers<D>(state, domain, peer, includeSelf);
    } else {
        const user = peer.identity.user;
        if (user) {
            return Array.from<PeerId>(state.users?.byName?.get(user) ?? []).concat(Array.from<PeerId>(state.services ?? []))
                .map((peerId: PeerId) => {
                    return byId(state, peerId);
                })
                .filter(some<Peer>)
                .filter((p: Peer): p is JoinedPeer<D> => {
                    return (c.peerInDomain(p, domain) && isPeerVisible(domain, peer, p)) || includeSelf === true && p.id === peer.id;
                });
        } else {
            return allVisiblePeers(state, domain, peer, includeSelf);
        }
    }
}

/*Generates the peer announcement messages for both sides*/
function announcePeerMessages(domainUri: DomainUri, existingMessages: SendMessageAction<'peer-added'>[], receiver: Source, newPeer: Peer, existingPeer: Peer): SendMessageAction<'peer-added'>[] {
    const existingIdentity = existingPeer.identity;
    const existingId = existingPeer.id;

    const newPeerId = newPeer.id;
    const isLocal = isLocalPeer(newPeer);
    return produce(existingMessages, (m) => {
        if (isLocal) {
            m.push(peerAdded(domainUri, receiver, newPeerId, existingId, existingIdentity, {local: isLocal}));
        }
        if (isLocalPeer(existingPeer)) {
            m.push(peerAdded(domainUri, existingPeer.source, existingId, newPeerId, newPeer.identity, {local: isLocal}));
        }
    });
}

export function announcePeer<D extends DomainKeys>(domainUri: DomainUri, domain: D, state: State, receiver: Source, peer: JoinedPeer<D>): SendMessageAction<'peer-added'>[] {
    return visiblePeers(state, domain, peer).reduce((messages, existingPeer) => {
        return announcePeerMessages(domainUri, messages, receiver, peer, existingPeer);
    }, [] as Array<SendMessageAction<'peer-added'>>);
}

/**
 * Peer is leaving the domain.
 *
 *        Updates the state and generates messages for all other local peers
 */
export function leaveDomain<D extends DomainKeys>(domainUri: DomainUri, domain: D, state: State, leavingPeer: JoinedPeer<D>, reason: Reason, isSourceRemoved: boolean): HandleReturnType {
    const peerId = leavingPeer.id;
    return [
        c.leaveDomain(state, peerId, domain),
        visiblePeers(state, domain, leavingPeer)
            .filter(isLocalPeer)
            .reduce((messages, removedFrom) => {
                messages = messages.concat(peerRemoved(domainUri, removedFrom.source, removedFrom.id, peerId, reason));
                if (!isSourceRemoved && isLocalPeer(leavingPeer)) {
                    messages = messages.concat(peerRemoved(domainUri, leavingPeer.source, peerId, removedFrom.id, reason));
                }
                return messages;
            }, [] as SendMessageAction<MessageType>[])
    ];
}
